import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { pubState } from "../../../../../../recoil/pub.atom";
import { usePaymentTypes } from "../../hooks/usePaymentTypes";
import { useControllerContext } from "../../../../hooks/controller-context";
import { useParticipantContext } from "../../hooks/participant-context";
import {
  EntryType,
  participantTournament,
  PaymentMethod,
  TournamentParticipantPaymentRequest,
} from "../../../../../../api/tournament-participant";
import {
  ControllerParticipantUserNameWrapper,
  ControllerParticipationContent,
  ControllerParticipationUserPoint,
  ControllerParticipationUserWrapper,
  ControllerParticipationWrapper,
} from "./style";
import { PaymentTypeInput } from "../payment-input";
import ControllerParticipationSearch from "./search";
import { ControllerSitInButtonGroup } from "../../style";
import { Const } from "../../../../../../common/const/const";
import { BuyInType } from "../../hooks/use-tournament-participant";
import { WideState } from "../../../../../../recoil/wide";
import { exchangePoint } from "../../../../../../utils/number";
import { isTournamentAccess } from "../../../../../../utils/owner";

export const ControllerParticipation = () => {
  const {
    participantMember,
    setParticipantMember,
    setIsBuyInPopup,
    reloadData,
    isBuyInType,
  } = useParticipantContext();
  const { data, level } = useControllerContext();
  const { payType, description } = usePaymentTypes();
  const { id } = useParams();
  const tournamentId = id?.replace(/[^0-9]/g, "");
  const isWide = useRecoilValue(WideState);
  // 지불할 포인트, 시드, 총 결제 금액
  const [payPoint, setPayPoint] = useState<number>(0);
  const [paySeed, setPaySeed] = useState<number>(0);
  const [totalPay, setTotalPay] = useState<number>(0);
  // 연속 클릭 방지
  const [isUpdate, setIsUpdate] = useState<boolean>(false);
  const pub = useRecoilValue(pubState);
  // 회원 포인트, 시드
  const point = useMemo(
    () => participantMember?.point ?? 0,
    [participantMember],
  );
  const seed = useMemo(() => participantMember?.seed ?? 0, [participantMember]);
  const [isHover, setIsHover] = useState<boolean>(false);

  useEffect(() => {
    if (data?.buyIn) setTotalPay(data?.buyIn);
  }, [data]);

  // 얼리버드 할인
  useEffect(() => {
    if (!data) return;
    if (data.tournamentDiscounts?.length > 0) {
      // 현재 시간에 맞는 할인 정보 찾기
      const discount = data.tournamentDiscounts.find(
        (item) => item.level === level,
      );
      const payment = payType.find((p) => p.type === PaymentMethod.DISCOUNT);

      if (discount && payment) {
        payment.set(discount.amount);
      }
    }
  }, []);

  // 유저참가
  const handleBuyIn = async (
    reqPayType?: TournamentParticipantPaymentRequest[],
  ) => {
    if (!pub || !tournamentId || !data) return;
    if (!isTournamentAccess(+tournamentId, pub.tournamentIds)) {
      alert(`토너먼트 ID ${tournamentId}에 대한 권한이 없습니다. `);
      return;
    }
    if (!participantMember) {
      alert("유저를 선택해주세요. ");
      return;
    }

    // 결제 request
    const paymentRequest: TournamentParticipantPaymentRequest[] =
      reqPayType || [];
    if (paymentRequest.length === 0) {
      for (const type of payType) {
        if (type.value === 0 || type.value === null) continue;
        paymentRequest.push({
          paymentMethod: type.type,
          paymentAmount: +type.value,
        });
      }
    }

    const sum = paymentRequest.reduce((a, b) => a + b.paymentAmount, 0);
    if (sum <= 0) {
      alert("총 결제 금액은 0보다 커야합니다. ");
      return;
    }

    if (data.buyIn !== sum) {
      alert("결제 금액을 확인해주세요. ");
      return;
    }

    // 연속 클릭 방지
    if (isUpdate) return;

    const request = {
      memberId: participantMember?.memberId,
      pubId: pub.id,
      tournamentId: +tournamentId,
      level: level === 0 ? 1 : level,
      entryType: EntryType.BUY_IN,
      paymentMethodRequests: paymentRequest,
      description,
    };
    try {
      setIsUpdate(true);
      await participantTournament(request);
      alert(`'${participantMember.nickname}'님이 참가되었습니다. `);
      setPayPoint(0);
      setIsBuyInPopup(false);
      reloadData();
    } catch (e: any) {
      setParticipantMember(null);
      if (e.status === 403) {
        alert(e.response.data.message);
        return;
      }
      alert(e.message);
    }
  };

  const canJoinMember = () => {
    if (totalPay === 0 && payPoint >= 0 && paySeed >= 0) return true;
    return false;
  };

  const userPoints = [
    {
      name: isWide ? `보유 매장 이용권` : "보유 E",
      value: +exchangePoint(point),
      pay: +exchangePoint(payPoint),
      unit: "E",
    },
    {
      name: isWide ? "보유 크레딧" : "보유 RB",
      value: seed ?? 0,
      pay: paySeed,
      unit: "RABBIT",
    },
  ];

  const handlePayFull = (index: number) => {
    if (!data) return;
    const buyIn = data?.buyIn ?? 0;

    payType.forEach((item, i) => {
      const isTarget = i === index;
      const valueToSet = isTarget ? buyIn : 0;

      item.set(valueToSet);

      if (item.unit === "E" && setPayPoint) {
        setPayPoint(valueToSet);
      } else if (item.unit === "RABBIT" && setPaySeed) {
        setPaySeed(isTarget ? buyIn / Const.SEED_RATE : 0);
      }
    });

    const paymentRequest: TournamentParticipantPaymentRequest[] = [];
    payType.forEach((item, i) => {
      const isTarget = i === index;
      const valueToSet = isTarget ? buyIn : 0;

      if (valueToSet === 0) return;
      paymentRequest.push({
        paymentMethod: item.type,
        paymentAmount: valueToSet,
      });
    });

    handleBuyIn(paymentRequest);
  };

  return (
    <ControllerParticipationWrapper
      isSearch={!!participantMember}
      isWide={isWide}
    >
      <div className="title">
        {isBuyInType(BuyInType.NORMAL) || isBuyInType(BuyInType.RESERVATION)
          ? "닉네임 검색"
          : "QR 싯인"}
      </div>
      {participantMember ? (
        <ControllerParticipationContent>
          {/* 유저 정보 */}
          <ControllerParticipationUserWrapper>
            <ControllerParticipantUserNameWrapper>
              <div className="name">
                {participantMember.nickname}
                {isWide
                  ? isBuyInType(BuyInType.RESERVATION) ||
                    isBuyInType(BuyInType.QR_RESERVATION)
                    ? " (예약회원)"
                    : ""
                  : ""}
              </div>
              {participantMember.memo && (
                <div className="more">
                  <img
                    alt="more"
                    src="/image-web/solution/more.png"
                    onMouseOver={() => setIsHover(true)}
                    onMouseOut={() => setIsHover(false)}
                  />
                  {isHover && (
                    <div className="memo">{participantMember.memo}</div>
                  )}
                </div>
              )}
            </ControllerParticipantUserNameWrapper>
            <ControllerParticipationUserPoint>
              {userPoints.map((item, i) => (
                <div className="point" key={i}>
                  <div className="name">{item.name}</div>
                  <div className="value">
                    {(item?.value - item.pay)?.toLocaleString()} {item.unit}
                  </div>
                </div>
              ))}
            </ControllerParticipationUserPoint>
          </ControllerParticipationUserWrapper>
          {/* 결제 input */}
          <PaymentTypeInput
            payType={payType}
            setTotalPay={setTotalPay}
            buyIn={data?.buyIn ?? 0}
            setPayPoint={setPayPoint}
            setPaySeed={setPaySeed}
            totalPay={totalPay}
            handleBuyIn={() => !isUpdate && handleBuyIn()}
            handlePayFull={handlePayFull}
          />
        </ControllerParticipationContent>
      ) : (
        // 유저 검색
        <ControllerParticipationSearch setMember={setParticipantMember} />
      )}
      <ControllerSitInButtonGroup>
        <div
          className={`button ${canJoinMember() ? "purple" : "gray"}`}
          onClick={() => !isUpdate && handleBuyIn()}
        >
          저장
        </div>
        <div
          className="button"
          onClick={() => {
            setIsBuyInPopup(false);
          }}
        >
          취소
        </div>
      </ControllerSitInButtonGroup>
    </ControllerParticipationWrapper>
  );
};
